import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { UsState } from '@client/graphql/__generated__/types';
import { GeocodingFeature } from '@mapbox/search-js-core';
import { Form, Formik } from 'formik';
import { FC, useState } from 'react';
import { GradientBorder } from '~/common/components/GradientBorder/GradientBorder';
import { AddressStep } from './AddressStep';
import {
  AddOffMarketListingStep,
  FIELD_TO_LABEL_MAP,
  INITIAL_VALUES,
  OffMarketListingFormData,
  REQUIRED_ADDRESS_FIELDS,
  REQUIRED_DETAILS_FIELDS,
} from './constants';
import { PropertyDetailsStep } from './PropertyDetailsStep';
import { useAddOffMarketListingsActions } from './useAddOffMarketListingsActions';

interface AddOffMarketListingModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialGeocode?: GeocodingFeature | null;
}

export const AddOffMarketListingModal: FC<AddOffMarketListingModalProps> = ({
  isOpen,
  onClose,
  initialGeocode,
}) => {
  const {
    isOpen: isTooltipOpen,
    onOpen: onTooltipOpen,
    onClose: onTooltipClose,
  } = useDisclosure();

  const [showAddressFields, setShowAddressFields] = useState(!!initialGeocode);

  const {
    handleSubmit,
    handleAddressNext,
    isLoading,
    step,
    setStep,
    setSelectedGeocode,
  } = useAddOffMarketListingsActions({
    initialGeocode,
    onClose,
  });

  const initialValues = {
    ...INITIAL_VALUES,
    ...{
      streetAddress: initialGeocode?.properties.context.address?.name || '',
      city: initialGeocode?.properties.context.place?.name || '',
      state:
        (
          initialGeocode?.properties.context.region as
            | { region_code: UsState | undefined }
            | undefined
        )?.region_code || undefined,
      zipCode: initialGeocode?.properties.context.postcode?.name || '',
      unit:
        (
          initialGeocode?.properties.context as {
            secondary_address: { name: string }; // Mapbox GeocodingFeatureContext type is not complete
          }
        )?.secondary_address?.name || '',
    },
  } as Partial<OffMarketListingFormData>;

  const handleClose = () => {
    setStep(AddOffMarketListingStep.ADDRESS);
    setShowAddressFields(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} size="lg" onClose={handleClose}>
      <ModalOverlay />
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        {(props) => {
          const values = props.values;

          const shouldDisableButton = () => {
            if (step === AddOffMarketListingStep.ADDRESS) {
              return !REQUIRED_ADDRESS_FIELDS.every((field) => values[field]);
            } else {
              return !REQUIRED_DETAILS_FIELDS.every((field) => values[field]);
            }
          };

          const isButtonDisabled = shouldDisableButton();

          const getTooltipLabel = () => {
            if (step === AddOffMarketListingStep.ADDRESS) {
              if (showAddressFields) {
                return (
                  <Flex direction="column">
                    {REQUIRED_ADDRESS_FIELDS.map((field) => {
                      if (!values[field]) {
                        return (
                          <Text key={field}>
                            {
                              FIELD_TO_LABEL_MAP[
                                field as keyof OffMarketListingFormData
                              ]
                            }{' '}
                            required
                          </Text>
                        );
                      }
                    })}
                  </Flex>
                );
              } else {
                return 'Enter the address of the property you want to add';
              }
            } else {
              return (
                <Flex direction="column">
                  {REQUIRED_DETAILS_FIELDS.map((field) => {
                    if (!values[field]) {
                      return (
                        <Text key={field}>
                          {
                            FIELD_TO_LABEL_MAP[
                              field as keyof OffMarketListingFormData
                            ]
                          }{' '}
                          required
                        </Text>
                      );
                    }
                  })}
                </Flex>
              );
            }
          };

          return (
            <Form>
              <ModalContent maxHeight="80vh">
                <ModalHeader>Add Off Market Listing</ModalHeader>

                <ModalCloseButton />
                <ModalBody overflow="auto" p={5}>
                  <VStack align="stretch" spacing={5}>
                    <GradientBorder alignItems="center" p={3}>
                      <Text bg="purpleGradient" bgClip="text">
                        Only you'll be able to see this listing
                      </Text>
                    </GradientBorder>

                    {step === AddOffMarketListingStep.ADDRESS && (
                      <AddressStep
                        setSelectedGeocode={setSelectedGeocode}
                        setShowAddressFields={setShowAddressFields}
                        showAddressFields={showAddressFields}
                      />
                    )}

                    {step === AddOffMarketListingStep.DETAILS && (
                      <PropertyDetailsStep />
                    )}
                  </VStack>
                </ModalBody>
                <ModalFooter bottom={0} p={3} position="sticky">
                  <Button size="sm" variant="ghost" onClick={handleClose}>
                    Cancel
                  </Button>

                  <Tooltip
                    isOpen={isTooltipOpen}
                    label={getTooltipLabel()}
                    placement="top"
                    onClose={onTooltipClose}
                  >
                    <span
                      tabIndex={0}
                      onClick={() => {
                        if (isButtonDisabled) {
                          onTooltipOpen();
                        }
                      }}
                      onMouseEnter={() => {
                        if (isButtonDisabled) {
                          onTooltipOpen();
                        }
                      }}
                      onMouseLeave={() => {
                        if (isTooltipOpen) {
                          onTooltipClose();
                        }
                      }}
                    >
                      <Button
                        isDisabled={isButtonDisabled}
                        isLoading={isLoading}
                        size="sm"
                        type={
                          step === AddOffMarketListingStep.DETAILS
                            ? 'submit'
                            : 'button'
                        }
                        onClick={
                          step === AddOffMarketListingStep.ADDRESS
                            ? () =>
                                void handleAddressNext(
                                  props.values,
                                  props.setFieldValue
                                )
                            : undefined
                        }
                      >
                        {step === AddOffMarketListingStep.DETAILS
                          ? 'Submit'
                          : 'Next'}
                      </Button>
                    </span>
                  </Tooltip>
                </ModalFooter>
              </ModalContent>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
